import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Share2, Calendar, Image as ImageIcon, Users, Settings, Megaphone, Lock, AlertTriangle, Plus, UserPlus, Plug, Unplug, CheckCircle2 } from "lucide-react";
import type { SocialPost, SocialAccount, SocialMediaAsset, ModuleGrant, SocialStorageQuota } from "@shared/schema";
import { PostComposerDialog } from "@/components/social/post-composer-dialog";
import { PostDetailDialog } from "@/components/social/post-detail-dialog";
import { CollaboratorInviteDialog } from "@/components/social/collaborator-invite-dialog";
import { CalendarView } from "@/components/social/calendar-view";
import { MediaUploaderCard } from "@/components/social/media-uploader-card";
import { ConnectAccountDialog } from "@/components/social/connect-account-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const PLATFORMS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "twitter", label: "Twitter / X" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "YouTube" },
  { key: "pinterest", label: "Pinterest" },
  { key: "threads", label: "Threads" },
];

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

const CONNECTABLE_PLATFORM_KEYS = new Set(["facebook", "instagram", "tiktok", "youtube"]);

export default function MarketingSocialPage() {
  const [tab, setTab] = useState("posts");
  const [composerOpen, setComposerOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [detailPost, setDetailPost] = useState<SocialPost | null>(null);
  const { toast } = useToast();

  // Surface OAuth callback feedback (?connected=facebook | ?connectError=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const err = params.get("connectError");
    const tabParam = params.get("tab");
    if (tabParam) setTab(tabParam);
    if (connected) {
      toast({ title: "Account connected", description: `Linked ${connected} successfully.` });
    }
    if (err) {
      const errorMessages: Record<string, string> = {
        access_revoked:
          "Your access to this workspace was revoked while connecting. Please try again from an authorized account.",
        session_mismatch:
          "OAuth session mismatch. Please start the connect flow again from the same browser session.",
        no_accounts:
          "No publishable accounts were returned by the platform. Make sure your account has at least one Page or channel.",
      };
      const description = errorMessages[err] ?? err;
      toast({ title: "Connect failed", description, variant: "destructive" });
    }
    if (connected || err || tabParam) {
      // Clear querystring so refreshes don't re-fire toasts.
      const url = new URL(window.location.href);
      ["connected", "connectError", "tab"].forEach((k) => url.searchParams.delete(k));
      window.history.replaceState({}, "", url.toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disconnectMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/social/accounts/${id}/disconnect`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/accounts"] });
      toast({ title: "Account disconnected" });
    },
    onError: (e: any) => {
      toast({ title: "Disconnect failed", description: e.message, variant: "destructive" });
    },
  });

  const { data: posts, isLoading: postsLoading } = useQuery<SocialPost[]>({
    queryKey: ["/api/social/posts"],
  });
  const { data: accounts, isLoading: accountsLoading } = useQuery<SocialAccount[]>({
    queryKey: ["/api/social/accounts"],
  });
  const { data: media, isLoading: mediaLoading } = useQuery<SocialMediaAsset[]>({
    queryKey: ["/api/social/media"],
  });
  const { data: grants, isLoading: grantsLoading } = useQuery<ModuleGrant[]>({
    queryKey: ["/api/social/grants"],
  });
  const { data: quota } = useQuery<SocialStorageQuota>({
    queryKey: ["/api/social/storage/quota"],
  });

  return (
    <div className="container mx-auto py-6 space-y-6 max-w-7xl" data-testid="page-marketing-social">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Share2 className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">Social Media</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Plan, draft, and review social posts across all your platforms in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setInviteOpen(true)} data-testid="button-invite-collaborator-header">
            <UserPlus className="h-4 w-4 mr-2" />
            Invite collaborator
          </Button>
          <Button onClick={() => setComposerOpen(true)} data-testid="button-new-post-header">
            <Plus className="h-4 w-4 mr-2" />
            New post
          </Button>
        </div>
      </div>

      <Alert className="border-amber-500/40 bg-amber-50 dark:bg-amber-950/20" data-testid="alert-coming-soon">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <AlertTitle className="text-amber-900 dark:text-amber-100 flex items-center gap-2">
          Live publishing — COMING SOON
          <Badge variant="outline" className="border-amber-500/50 text-amber-800 dark:text-amber-200 bg-amber-100/50 dark:bg-amber-900/30">
            Beta
          </Badge>
        </AlertTitle>
        <AlertDescription className="text-amber-800 dark:text-amber-200/80">
          You can already build your library, draft posts, schedule them, and invite outside collaborators (like a freelance video editor) using the access-grant system. Direct publishing to Facebook, Instagram, TikTok, LinkedIn, X, YouTube, Pinterest, and Threads will switch on once each platform's review is approved. Anything you draft now will be ready to launch the moment that goes live.
        </AlertDescription>
      </Alert>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-6 w-full" data-testid="tabs-social">
          <TabsTrigger value="posts" data-testid="tab-posts">
            <Megaphone className="h-4 w-4 mr-2" />Posts
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <Calendar className="h-4 w-4 mr-2" />Calendar
          </TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">
            <ImageIcon className="h-4 w-4 mr-2" />Media
          </TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">
            <Share2 className="h-4 w-4 mr-2" />Accounts
          </TabsTrigger>
          <TabsTrigger value="collaborators" data-testid="tab-collaborators">
            <Users className="h-4 w-4 mr-2" />Collaborators
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings className="h-4 w-4 mr-2" />Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle>Drafts &amp; scheduled posts</CardTitle>
                <CardDescription>Everything in your queue across every platform.</CardDescription>
              </div>
              <Button size="sm" onClick={() => setComposerOpen(true)} data-testid="button-new-post-tab">
                <Plus className="h-4 w-4 mr-2" />
                New post
              </Button>
            </CardHeader>
            <CardContent>
              {postsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : posts && posts.length > 0 ? (
                <ul className="space-y-2">
                  {posts.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between border rounded-md p-3 hover-elevate cursor-pointer"
                      onClick={() => setDetailPost(p)}
                      data-testid={`row-post-${p.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{p.campaignTag || p.internalNote?.slice(0, 60) || "Untitled draft"}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.scheduledAt ? `Scheduled for ${new Date(p.scheduledAt).toLocaleString()}` : "Not scheduled"}
                        </div>
                      </div>
                      <Badge variant="outline" className="ml-2">{p.status}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground py-8 text-center" data-testid="text-empty-posts">
                  No posts yet. Click <span className="font-medium">New post</span> above to draft your first one.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          {postsLoading ? (
            <Skeleton className="h-96 w-full" />
          ) : (
            <CalendarView posts={posts || []} onSelectPost={setDetailPost} />
          )}
        </TabsContent>

        <TabsContent value="media" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle>Media library</CardTitle>
                <CardDescription>Photos, videos, and graphics — Drive-backed (free) or hosted (paid).</CardDescription>
              </div>
              <MediaUploaderCard />
            </CardHeader>
            <CardContent>
              {mediaLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : media && media.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {media.map((m) => (
                    <div key={m.id} className="border rounded-md p-2 text-xs" data-testid={`tile-media-${m.id}`}>
                      <div className="aspect-square bg-muted rounded mb-2 flex items-center justify-center overflow-hidden">
                        {m.mimeType?.startsWith("image/") && m.objectKey ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={m.objectKey} alt={m.altText || m.fileName} className="object-cover w-full h-full" />
                        ) : (
                          <ImageIcon className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <div className="truncate font-medium">{m.fileName}</div>
                      <div className="text-muted-foreground">{m.storageMode} · {m.tier}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground py-8 text-center" data-testid="text-empty-media">
                  No media uploaded yet. Click <span className="font-medium">Upload media</span> to add your first file.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle>Connected accounts</CardTitle>
                <CardDescription>Link each platform you want to publish to. Drafts can be queued before any account is approved for live publishing.</CardDescription>
              </div>
              <Button size="sm" onClick={() => setConnectOpen(true)} data-testid="button-connect-account">
                <Plug className="h-4 w-4 mr-2" />
                Connect account
              </Button>
            </CardHeader>
            <CardContent>
              {accountsLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {PLATFORMS.map((plat) => {
                    const connected = accounts?.find((a) => a.platform === plat.key && a.status === "active");
                    const isConnectable = CONNECTABLE_PLATFORM_KEYS.has(plat.key);
                    const approvedForPublish = (connected?.metadata as any)?.publishingApproved === true;
                    return (
                      <div key={plat.key} className="flex items-center justify-between border rounded-md p-3 gap-3" data-testid={`row-account-${plat.key}`}>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium flex items-center gap-2">
                            {plat.label}
                            {connected && approvedForPublish && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-green-500/40 text-green-700 dark:text-green-400">
                                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                                Publish-approved
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {connected
                              ? `Connected as ${connected.displayName || connected.externalUsername || connected.externalAccountId}` +
                                (connected.lastSyncAt ? ` · Last synced ${new Date(connected.lastSyncAt).toLocaleString()}` : "")
                              : isConnectable
                                ? "Not connected"
                                : "Coming soon"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {connected ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => disconnectMutation.mutate(connected.id)}
                              disabled={disconnectMutation.isPending}
                              data-testid={`button-disconnect-${plat.key}`}
                            >
                              <Unplug className="h-3.5 w-3.5 mr-1.5" />
                              Disconnect
                            </Button>
                          ) : isConnectable ? (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => setConnectOpen(true)}
                              data-testid={`button-connect-${plat.key}`}
                            >
                              <Plug className="h-3.5 w-3.5 mr-1.5" />
                              Connect
                            </Button>
                          ) : (
                            <Badge variant="outline" className="gap-1">
                              <Lock className="h-3 w-3" />
                              Coming soon
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 text-xs text-muted-foreground">
                Need to remove your data entirely? Visit the{" "}
                <a href="/data-deletion" className="underline hover:no-underline" data-testid="link-data-deletion">data deletion page</a>.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="collaborators" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div className="space-y-1">
                <CardTitle>External collaborators</CardTitle>
                <CardDescription>
                  Invite outside helpers (like a freelance video editor) with scoped access — they only see Social Media, nothing else in the CRM.
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => setInviteOpen(true)} data-testid="button-invite-collaborator-tab">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite
              </Button>
            </CardHeader>
            <CardContent>
              {grantsLoading ? (
                <Skeleton className="h-20 w-full" />
              ) : grants && grants.length > 0 ? (
                <ul className="space-y-2">
                  {grants.map((g) => (
                    <li key={g.id} className="flex items-center justify-between border rounded-md p-3" data-testid={`row-grant-${g.id}`}>
                      <div>
                        <div className="font-medium">{g.inviteEmail || g.userId || "Pending invite"}</div>
                        <div className="text-xs text-muted-foreground">{g.accessLevel} · {g.status}</div>
                      </div>
                      <Badge variant="outline">{g.status}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground py-8 text-center" data-testid="text-empty-grants">
                  No collaborators invited yet. Click <span className="font-medium">Invite</span> to share scoped access.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Storage &amp; settings</CardTitle>
              <CardDescription>Quota usage and storage mode (Drive vs hosted) defaults.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-4 border rounded-md p-4" data-testid="card-quota">
                <div>
                  <div className="text-muted-foreground text-xs">Plan grant</div>
                  <div className="font-medium">{formatBytes(Number(quota?.planGrantBytes ?? 0))}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Purchased</div>
                  <div className="font-medium">{formatBytes(Number(quota?.purchasedBytes ?? 0))}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Used</div>
                  <div className="font-medium">{formatBytes(Number(quota?.usedBytes ?? 0))}</div>
                </div>
              </div>
              <div className="text-muted-foreground text-xs">
                Detailed settings (storage mode, lifecycle, white-label) ship in the next iteration.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PostComposerDialog open={composerOpen} onOpenChange={setComposerOpen} />
      <PostDetailDialog
        open={!!detailPost}
        onOpenChange={(o) => { if (!o) setDetailPost(null); }}
        post={detailPost}
      />
      <CollaboratorInviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <ConnectAccountDialog open={connectOpen} onOpenChange={setConnectOpen} />
    </div>
  );
}
